import { ReactNode } from "react";
import { cn } from "@/lib/utils";

interface PageHeaderProps {
  title: string;
  description?: ReactNode;
  eyebrow?: ReactNode;
  actions?: ReactNode;
  align?: "left" | "center";
  className?: string;
}

/**
 * Shared page header, Apple-minimal, consistent across all routes.
 * Title uses font-display, description in muted-foreground.
 */
export function PageHeader({
  title,
  description,
  eyebrow,
  actions,
  align = "left",
  className,
}: PageHeaderProps) {
  const centered = align === "center";
  return (
    <header
      className={cn(
        "mb-8 flex flex-col gap-4 md:flex-row md:items-end md:justify-between",
        "animate-in fade-in slide-in-from-bottom-3 duration-300",
        centered && "md:flex-col md:items-center text-center",
        className
      )}
    >
      <div className={cn("min-w-0", centered && "mx-auto max-w-2xl")}>
        {eyebrow && (
          <div className="mb-2 text-xs uppercase tracking-[0.14em] text-muted-foreground/80">
            {eyebrow}
          </div>
        )}
        <h1 className="font-display text-3xl md:text-4xl font-semibold tracking-tight text-foreground">
          {title}
        </h1>
        {description && (
          <p className="mt-2 text-base md:text-lg text-muted-foreground leading-relaxed">
            {description}
          </p>
        )}
      </div>
      {actions && (
        <div className={cn("flex flex-wrap items-center gap-2", centered && "justify-center")}>
          {actions}
        </div>
      )}
    </header>
  );
}

export default PageHeader;
